import type { EmployeeExperienceResponse, SourceTypeCode } from '../../api/types';
import { formatPeriod, formatYears } from './factDisplay';
import { EditExperienceControl } from './EmployeeFactsCapture';
import styles from './FactRecords.module.css';

/**
 * The Experience section (docs/DOMAIN_MODEL.md §2.8).
 *
 * ---------------------------------------------------------------------
 * THE TOTAL THAT IS NOT HERE
 *
 * The obvious thing to put at the top of this section is a headline —
 * "21.5 years' experience". It is deliberately absent, and it is worth
 * being exact about why, because the number is easy to compute and looks
 * authoritative.
 *
 * Experience records are per-area and can overlap in time: Amara's seven
 * years of Field Operations and her seven years of Team Supervision are
 * partly the same seven years. Adding recorded durations therefore
 * produces a figure that overstates a career, and does so by an amount
 * that varies per person. Role Fit does sum them — inside a single-area
 * threshold comparison, where the simplification is bounded and
 * documented (docs/DERIVED_LOGIC.md §21) — and Mission 008 declined to
 * re-present that sum as a standalone fact for exactly this reason
 * (docs/API.md §6.1). The API returns no total, and this section does not
 * invent one.
 *
 * Nothing else is derived here either: no seniority, no proficiency, no
 * readiness, no capability. Experience is recorded exposure. What it
 * amounts to is a judgement for a person to make with the records in
 * front of them.
 * ---------------------------------------------------------------------
 */
export function ExperienceSection({
  data,
  employeeId,
  viewerCanAssessAs,
  onChanged,
}: {
  data: EmployeeExperienceResponse;
  employeeId: string;
  viewerCanAssessAs: SourceTypeCode | null;
  onChanged: () => void;
}) {
  return (
    <ul className={styles.list}>
      {data.experience.map((entry) => (
        <li key={entry.id} className={styles.record}>
          <div className={styles.primary}>
            <span className={styles.name}>{entry.experienceArea.name}</span>
            {entry.experienceArea.category ? (
              <span className={styles.category}>{entry.experienceArea.category}</span>
            ) : null}
            {entry.endedOn === null && entry.startedOn !== null ? (
              <span className={styles.marker} title="This record has no recorded end date.">
                Open-ended record
              </span>
            ) : null}
            {entry.experienceArea.status === 'deprecated' ? (
              <span
                className={styles.marker}
                title="This experience area has been retired from the organisation's library. The record is still held."
              >
                Retired from the experience library
              </span>
            ) : null}
          </div>

          <dl className={styles.facts}>
            <div>
              <dt>Period</dt>
              <dd>{formatPeriod(entry.startedOn, entry.endedOn)}</dd>
            </div>
            <div>
              <dt>Recorded duration</dt>
              <dd>
                {entry.durationYears === null ? (
                  <span className={styles.absent}>Not recorded</span>
                ) : (
                  formatYears(entry.durationYears)
                )}
              </dd>
            </div>
          </dl>

          {entry.contextNotes ? <p className={styles.notes}>{entry.contextNotes}</p> : null}

          {viewerCanAssessAs !== null ? (
            <EditExperienceControl employeeId={employeeId} experience={entry} onChanged={onChanged} />
          ) : null}
        </li>
      ))}
    </ul>
  );
}
